import { call, put, takeEvery } from 'redux-saga/effects';
import {
  VIEWING_FETCH_ANNOTATIONS_BEGIN,
  VIEWING_FETCH_ANNOTATIONS_SUCCESS,
  VIEWING_FETCH_ANNOTATIONS_FAILURE,
  VIEWING_FETCH_ANNOTATIONS_DISMISS_FEEDBACK,
  VIEWING_FILTER_ANNOTATIONS,
  VIEWING_FILTER_ANNOTATIONS_FOR_COMPARE,
} from './constants';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { withCurrentCaseId, withCurrentUserId } from 'common/selectors';
import {
  DEFAULT_ANNOTATION_FILTER,
  createFilter,
  filterAnnotations as filter,
} from './filterAnnotations';

export const fetchAnnotations = createPromiseAction(VIEWING_FETCH_ANNOTATIONS_BEGIN);

export function dismissFetchAnnotationsFeedback() {
  return {
    type: VIEWING_FETCH_ANNOTATIONS_DISMISS_FEEDBACK,
  };
}

// worker Saga: will be fired on VIEWING_FETCH_ANNOTATIONS_BEGIN actions
export function* doFetchAnnotations(action) {
  const {
    payload: {
      caseId,
      file,
      privateOnly,
      currentUserId: userId,
      forCompare,
      presentView = false,
    },
  } = action;

  const query = new URLSearchParams();
  if (privateOnly) {
    query.set('privateOnly', 'true');
  }
  if (presentView) {
    query.set('presentView', 'true');
  }

  const queryString = query.toString();
  const res = yield api.get(
    `/cases/${caseId}/files/${file}/annotations${queryString ? `?${queryString}` : ''}`,
  );

  if (res && res.error) {
    yield put({
      type: VIEWING_FETCH_ANNOTATIONS_FAILURE,
      feedback: {
        message: 'feedback.fetchAnnotationsFailure',
        error: res.error,
        retryAction: action,
      },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  // Dispatch success action out of try/catch so that render errors are not catched.
  yield put({
    type: VIEWING_FETCH_ANNOTATIONS_SUCCESS,
    data: res,
    userId,
    forCompare: !!forCompare,
  });

  yield call(resolvePromiseAction, action, res);
}

/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchFetchAnnotations() {
  // Allow concurrent requests for main and compare viewers
  yield takeEvery(fetchAnnotations, withCurrentUserId(withCurrentCaseId(doFetchAnnotations)));
}

export function useFetchAnnotations() {
  const dispatch = useDispatch();

  const { fetchAnnotationsPending, fetchAnnotationsFeedback, annotations } = useSelector(
    (state) => ({
      fetchAnnotationsPending: state.viewing.fetchAnnotationsPending,
      fetchAnnotationsFeedback: state.viewing.fetchAnnotationsFeedback,
      annotations: state.viewing.annotations,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(fetchAnnotations(...args));
    },
    [dispatch],
  );

  return {
    annotations,
    fetchAnnotations: boundAction,
    fetchAnnotationsPending,
    fetchAnnotationsFeedback,
  };
}

export function filterAnnotations(updatedFilters, userId, forCompare = false) {
  return {
    type: forCompare ? VIEWING_FILTER_ANNOTATIONS_FOR_COMPARE : VIEWING_FILTER_ANNOTATIONS,
    updatedFilters,
    userId,
  };
}

// Redux reducer
export function reducer(state, action) {
  switch (action.type) {
    case VIEWING_FETCH_ANNOTATIONS_BEGIN + '.TRIGGER':
      return {
        ...state,
        fetchAnnotationsPending: true,
        fetchAnnotationsFeedback: null,
      };

    case VIEWING_FETCH_ANNOTATIONS_SUCCESS:
      if (action.forCompare) {
        return {
          ...state,
          fetchAnnotationsPending: false,
          fetchAnnotationsFeedback: action.feedback,
          annotationsForCompare: action.data,
          filteredAnnotationsForCompare: filter(
            action.data,
            state.updatedFiltersForCompare || DEFAULT_ANNOTATION_FILTER,
            action.userId,
          ),
          updatedFiltersForCompare: createFilter(
            action.data,
            state.updatedFiltersForCompare || DEFAULT_ANNOTATION_FILTER,
          ),
        };
      }
      return {
        ...state,
        fetchAnnotationsPending: false,
        fetchAnnotationsFeedback: action.feedback,
        annotations: action.data,
        filteredAnnotations: filter(
          action.data,
          state.updatedFilters || DEFAULT_ANNOTATION_FILTER,
          action.userId,
        ),
        updatedFilters: createFilter(action.data, state.updatedFilters || DEFAULT_ANNOTATION_FILTER),
      };

    case VIEWING_FILTER_ANNOTATIONS:
      return {
        ...state,
        filteredAnnotations: filter(state.annotations, action.updatedFilters, action.userId),
        updatedFilters: action.updatedFilters,
      };
    case VIEWING_FILTER_ANNOTATIONS_FOR_COMPARE:
      return {
        ...state,
        filteredAnnotationsForCompare: filter(
          state.annotationsForCompare,
          action.updatedFilters,
          action.userId,
        ),
        updatedFiltersForCompare: action.updatedFilters,
      };

    case VIEWING_FETCH_ANNOTATIONS_FAILURE:
      return {
        ...state,
        fetchAnnotationsPending: false,
        fetchAnnotationsFeedback: action.feedback,
      };

    case VIEWING_FETCH_ANNOTATIONS_DISMISS_FEEDBACK:
      return {
        ...state,
        fetchAnnotationsFeedback: null,
      };

    default:
      return state;
  }
}
